<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { type CharacterActionLog } from '@/api/dx-backend';
import RPGActionLogRow from './RPGActionLogRow.vue';
import RPGActionLogModal from './RPGActionLogModal.vue';
import CodexHeading from '@/components/CodexRail/CodexHeading.vue';
import CodexSpine from '@/components/CodexRail/CodexSpine.vue';
import {
  collectActorIds,
  ensureActors,
  recentCycleActions,
  sortByRecency
} from './actionLogShared';

const props = withDefaults(defineProps<{
  actions: Array<CharacterActionLog>,
  currentCycleNumber?: number | null,
  selfCharacterId?: string | null,
  currentPhase?: string | null
}>(), {
  actions: () => ([]),
  currentCycleNumber: null,
  selfCharacterId: null,
  currentPhase: null
});

defineEmits<{
  'character-hover': [payload: { charId: string; event: MouseEvent }];
  'character-hover-leave': [];
  'character-selected': [charId: string];
}>();

const { t } = useI18n();

const modalOpen = ref(false);
const expandButton = ref<HTMLButtonElement | null>(null);

watch(() => props.actions, (list) => {
  ensureActors(collectActorIds(list || []));
}, { immediate: true });

const digestCycle = computed<number | null>(() => {
  if (props.currentCycleNumber !== null && props.currentCycleNumber !== undefined) {
    return props.currentCycleNumber;
  }
  const numbers = props.actions.map((action) => action.cycle?.number ?? 0);
  return numbers.length ? Math.max(...numbers) : null;
});

const cycleActions = computed(() => recentCycleActions(props.actions));

/* Every action in the window is rendered and the list scrolls: measuring how many rows
   fit made the column resize itself, and the count flapped on every layout change. */
const visibleRows = computed(() => sortByRecency(cycleActions.value));

const openModal = () => {
  modalOpen.value = true;
};

const closeModal = () => {
  modalOpen.value = false;
  nextTick(() => expandButton.value?.focus());
};
</script>

<template>
  <div class="digest" role="region" :aria-label="t('actionLog.digest.title')">
    <CodexHeading :label="t('actionLog.digest.railTitle')" tone="cyan" spine="node"/>

    <div class="digest-body">
      <div class="digest-list">
        <RPGActionLogRow
            v-for="action in visibleRows"
            :key="action.id"
            :action="action"
            :self-character-id="selfCharacterId"
            @open="openModal"
        />
        <p v-if="!visibleRows.length" class="digest-empty-text">{{ t('actionLog.digest.none') }}</p>
      </div>
    </div>

    <button ref="expandButton" type="button" class="digest-foot" @click="openModal">
      <CodexSpine variant="tail"/>
      <span class="digest-foot-main">
        <span class="digest-foot-rule" aria-hidden="true"></span>
        <span class="digest-foot-row">
          <span class="digest-foot-text">{{ t('actionLog.digest.openFull') }}</span>
          <i class="fa-solid fa-chevron-right digest-foot-chevron" aria-hidden="true"></i>
        </span>
      </span>
    </button>

    <RPGActionLogModal
        :open="modalOpen"
        :actions="actions"
        :self-character-id="selfCharacterId"
        :current-cycle-number="digestCycle"
        @close="closeModal"
        @character-hover="$emit('character-hover', $event)"
        @character-hover-leave="$emit('character-hover-leave')"
        @character-selected="$emit('character-selected', $event)"
    />
  </div>
</template>

<style scoped>
.digest {
  --lg-ink: #e8eef5;
  --lg-dim: rgba(232, 238, 245, 0.42);
  --lg-gold: #fada95;
  --lg-dmg: #ff4d5e;
  --lg-heal: #4ade80;
  --lg-buff: #ffd147;
  --lg-debuff: #c77dff;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  text-transform: none;
}

/* A floor keeps the footer still for ordinary action counts; the ceiling hands
   anything longer to the scroller instead of growing the column. */
.digest-body {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  min-height: 6.5rem;
  max-height: 24rem;
  min-width: 0;
}

.digest-list {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  padding-top: 0.75rem;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(96, 166, 184, 0.28) transparent;
}

.digest-list::-webkit-scrollbar {
  width: 3px;
}

.digest-list::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: rgba(96, 166, 184, 0.28);
}

/* One unbroken shaft behind the rows, so the ornament never gaps where the list runs out. */
.digest-body::before {
  content: '';
  filter: var(--codex-halo-drop, drop-shadow(0 1px 2px rgba(0, 10, 14, 0.9)));
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(var(--codex-spine-w, 16px) / 2 - 1.5px);
  width: 3px;
  opacity: 0.7;
  color: var(--codex-spine, rgba(78, 154, 170, 0.9));
  background: linear-gradient(
      to right,
      currentColor 0 1px,
      transparent 1px 2px,
      currentColor 2px 3px
  );
}

.digest-empty-text {
  margin: 0.6rem 0 0;
  padding-left: calc(var(--codex-spine-w, 16px) + var(--codex-spine-gap, 1.2rem));
  font-size: 0.78rem;
  color: rgba(179, 193, 200, 0.6);
  text-shadow: var(--codex-halo, 0 1px 2px rgba(0, 10, 14, 0.95), 0 0 8px rgba(0, 10, 14, 0.85));
}

.digest-foot {
  display: flex;
  flex: 0 0 auto;
  gap: var(--codex-spine-gap, 1.6rem);
  align-items: stretch;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}

.digest-foot:focus-visible {
  outline: none;
}

.digest-foot-main {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.7rem;
  min-width: 0;
  padding-top: 0.5rem;
}

.digest-foot-rule {
  height: 1px;
  filter: var(--codex-halo-drop, drop-shadow(0 1px 2px rgba(0, 10, 14, 0.9)));
  background: linear-gradient(
      to right,
      rgba(140, 190, 205, 0) 0,
      rgba(140, 190, 205, 0.45) 12%,
      rgba(140, 190, 205, 0.28) 70%,
      rgba(140, 190, 205, 0) 100%
  );
}

.digest-foot-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.digest-foot-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  font-size: 0.74rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(130, 200, 214, 0.98);
  text-shadow: var(--codex-halo, 0 1px 2px rgba(0, 10, 14, 0.95), 0 0 8px rgba(0, 10, 14, 0.85));
  transition: color 0.14s ease;
}

.digest-foot-chevron {
  flex: 0 0 auto;
  font-size: 0.85rem;
  color: rgba(48, 226, 234, 0.9);
  text-shadow: 0 0 0.4rem rgba(48, 226, 234, 0.5), var(--codex-halo, 0 1px 2px rgba(0, 10, 14, 0.95), 0 0 8px rgba(0, 10, 14, 0.85));
  transition: transform 0.14s ease;
}

.digest-foot:hover .digest-foot-text,
.digest-foot:focus-visible .digest-foot-text {
  color: #a9e9f2;
}

.digest-foot:hover .digest-foot-chevron,
.digest-foot:focus-visible .digest-foot-chevron {
  transform: translateX(3px);
}

@media (prefers-reduced-motion: reduce) {
  .digest-foot-text,
  .digest-foot-chevron {
    transition: none;
  }

  .digest-foot:hover .digest-foot-chevron {
    transform: none;
  }
}
</style>
